<script setup lang="ts" generic="TAnswer extends IAnswer">
  import { IValidation } from "@/view-models/form.model"
  import ErrorMessage from "./ErrorMessage.vue"

  export interface IAnswer {
    label: string
    value: string | number | boolean
  }

  const modelValue = defineModel<string | number | boolean | null>({
    default: null,
  })

  const props = withDefaults(
    defineProps<{
      label?: string
      info?: string
      id: string
      name: string
      answers: TAnswer[]
      validation?: IValidation
      column?: boolean
      wide?: boolean
      noMargin?: boolean
    }>(),
    {
      label: "",
      info: "",
      id: createUuid(),
      name: "",
      answers: () => [],
      validation: undefined,
      column: false,
      wide: false,
      noMargin: false,
    }
  )

  const uniqueId = (value: string | number | boolean) => {
    return props.name + "-" + value
  }

  const isInvalid = computed(() => {
    if (props.validation?.model && props.validation?.submitted) {
      return true
    }

    return false
  })
</script>

<template>
  <div class="my-4">
    <slot
      name="label"
      v-bind="{ isInvalid }"
    >
      <label
        v-if="label"
        :class="{ 'text-danger': isInvalid }"
        class="font-bold"
        :for="id"
        v-text="label"
      ></label>
    </slot>
    <slot
      name="info"
      v-bind="{ isInvalid }"
    >
      <p
        v-if="info"
        :class="{ 'text-danger': isInvalid }"
        class="form-info-text"
        v-text="info"
      ></p>
    </slot>

    <div
      :id="id"
      :class="[column ? 'column' : 'row', noMargin ? 'mb-0' : '']"
    >
      <slot
        name="input"
        v-bind="{ isInvalid }"
      >
        <div
          v-for="item in answers"
          :key="item.label"
          :class="{ 'wide-block': wide }"
        >
          <div
            :class="{
              active: modelValue === item.value,
              error: isInvalid,
            }"
            class="flex flex-row items-center"
          >
            <input
              :id="uniqueId(item.value)"
              v-model="modelValue"
              type="radio"
              :value="item.value"
              :name="name"
              tabindex="0"
            />
            <label
              class="ml-2"
              :for="uniqueId(item.value)"
              v-text="item.label"
            ></label>
          </div>
        </div>
      </slot>
      <slot
        name="error"
        v-bind="{ isInvalid }"
      >
        <error-message
          v-if="validation?.model && validation?.submitted"
          :message="validation?.message"
          only-text
        />
      </slot>
    </div>
  </div>
</template>
